import React, { useEffect, useState } from "react";

import { INVITATION_OCCASION, Invitations } from "../../../store/services/invitations/dto/invitations.dto";
import { trackEvent } from "../../../utils/analytics";
import { getWeddingDate } from "../../../utils/formatDate";
import ShareUi from "./ShareUI";
import ShareByQRModal from "./ShareByQRModal";
import { handleCopyToClipboard } from "../../../utils/copyToClipboard";
import { useUpdateInvitationHTMLMutation } from "../../../store/services/invitations/invitations.service";

const getOccasionType = (data: Invitations, isEng: boolean) => {
	switch (data?.occasion) {
		case INVITATION_OCCASION.ENGAGEMENT: {
			return isEng ? "Engagement" : "خِطبه";
		}
		case INVITATION_OCCASION.KATB_KTAB: {
			return isEng ? "Akd Karaan" : "عقد قران";
		}
		default: {
			return isEng ? "Wedding" : "زِفاف";
		}
	}
};

const ShareWeddingInvitationModal = ({
	data,
	open,
	onClose
}: {
	data: Invitations;
	open: boolean;
	onClose: () => void;
}) => {
	const [shareByQR, setShareByQR] = useState(false);
	const [callUpdateHTML, { isLoading }] = useUpdateInvitationHTMLMutation();
	const updatedTimeStamp = new Date(data?.updatedAt || "").getTime();
	const shareUrl = `${process.env.REACT_APP_SHARE || "http://localhost:3000"}/weddingInvitation/${data._id}/index-${updatedTimeStamp}.html`;

	useEffect(() => {
		callUpdateHTML({ _id: data._id });
	}, [data._id]);

	const handleShareToWhatsApp = () => {
		trackEvent("share_whatsapp_invitation", {
			scope: "Invitations",
			description: "Share WhatsApp Invitation"
		});
		const occasionType = getOccasionType(data, data.language === "EN");
		const message =
			data.language === "EN"
				? `Join us on our special day - ${occasionType} — ${getWeddingDate(data.wedding_date, data.language, data.timezone)}   Check it out: ${shareUrl}`
				: `شاركونا يومنا البهـيج - ${occasionType} - ${getWeddingDate(data.wedding_date, data.language, data.timezone)}
${shareUrl}`;

		const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
		window.open(whatsappUrl, "_blank");
	};

	const handleFacebookShare = () => {
		trackEvent("share_facebook_invitation", {
			scope: "Invitations",
			description: "Share Facebook Invitation"
		});
		const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
		window.open(facebookShareUrl, "_blank");
	};

	const handleFaceBookMessengerShare = async () => {
		const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
		const url = shareUrl;

		trackEvent("share_fb_messenger_invitation", {
			scope: "Invitations",
			description: "Share FB Messenger Invitation"
		});
		const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);

		if (isMobile) {
			const messengerDeepLink = `fb-messenger://share?link=${encodeURIComponent(url)}&app_id=${appId}`;

			window.location.href = messengerDeepLink;
			return;
		}

		const messengerShareUrl = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(
			url
		)}&app_id=${appId}&redirect_uri=${encodeURIComponent(url)}`;

		window.open(messengerShareUrl, "_blank", "noopener,noreferrer");
	};

	const handleTwitterShare = () => {
		const occasionType = getOccasionType(data, data.language === "EN");
		const tweetText =
			data.language === "EN"
				? `Join us on our special day - ${occasionType} — ${getWeddingDate(data.wedding_date, data.language, data.timezone)}`
				: `شاركونا يومنا البهـيج - ${occasionType} - ${getWeddingDate(data.wedding_date, data.language, data.timezone)}`;
		const hashtags = "Wedding,event,WeddingInvitation,EngagementInvitation,OurBigDay,HappilyEverAfter";

		trackEvent("share_X_invitation", {
			scope: "Invitations",
			description: "Share X Invitation"
		});
		const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags}`;
		window.open(twitterUrl, "_blank");
	};

	const handleDeviceShare = async () => {
		try {
			if (navigator.share) {
				await navigator.share({
					title: "Wedding Invitation",
					url: shareUrl
				});
			}
		} catch (err) {
			console.info("Share failed:", err);
		}
	};

	return (
		<>
			<ShareUi
				open={open}
				onClose={onClose}
				handleTwitterShare={handleTwitterShare}
				handleShareToWhatsApp={handleShareToWhatsApp}
				handleCopyToClipboard={() => handleCopyToClipboard(shareUrl)}
				handleFacebookShare={handleFacebookShare}
				handleFaceBookMessengerShare={handleFaceBookMessengerShare}
				shareUrl={shareUrl}
				enableQRCode={true}
				openQRCode={() => setShareByQR(true)}
				handleDeviceShare={handleDeviceShare}
				loading={isLoading}
			/>
			{shareByQR && (
				<ShareByQRModal
					id={data._id}
					open={shareByQR}
					qr_url={data?.invitation_qr_code}
					onClose={() => setShareByQR(false)}
					invitation_url={true}
				/>
			)}
		</>
	);
};

export default ShareWeddingInvitationModal;
